import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, AlertCircle, UserMinus, ArrowRightLeft, UserPlus } from 'lucide-react';
import { toast } from 'react-toastify';

import { Button } from '../../ui-v2';
import { Modal, Table, Select, Badge, EmptyState } from '../../ui-v2.5';
import { getResumenCampana, anadirAlLote, quitarDelLote, moverEntreLotes } from '../api';
import SelectorPersonas from '../wizard/SelectorPersonas';
import { MODALIDAD, etiquetaDe } from '../compartidos/etiquetasEstado';

/**
 * ModalPersonasLote — editar las personas de UN lote, con la campaña en curso (DS v2).
 *
 * Es la única puerta para cambiar personas con la campaña Activa o Pausada:
 *   · Añadir: el mismo selector que el lote nuevo (no ofrece a quien ya está en la campaña) y la
 *     misma validación; aviso a las nuevas si la campaña está Activa (en Pausada, al reanudar).
 *   · Quitar: una persona con la intervención realizada no se quita; si alguna tiene cita viva, se
 *     pide confirmación (se cancela la cita y se avisa a ella y al técnico).
 *   · Mover a otro lote de la campaña: conserva estado y cita.
 * Cada cambio deja historial. El servidor lo comprueba todo igual; aquí sólo se explica.
 * Los nombres se pintan como texto.
 */

const ESTADO = {
  pendiente_reserva: { texto: 'Sin cita', tono: 'waiting' },
  cita_reservada: { texto: 'Con cita', tono: 'progress' },
  realizado: { texto: 'Realizado', tono: 'active' },
  cancelado_usuario: { texto: 'Cancelado', tono: 'expired' },
  cancelado_sistemas: { texto: 'Cancelado', tono: 'expired' },
};

export default function ModalPersonasLote({ open, campana, lote, lotes, onClose, onCambio }) {
  const [participantes, setParticipantes] = useState(null);
  const [seleccion, setSeleccion] = useState([]);
  const [destino, setDestino] = useState('');
  const [nuevas, setNuevas] = useState({ ids: [], cache: {} });
  const [impacto, setImpacto] = useState(null); // citas que cancelaría quitar
  const [trabajando, setTrabajando] = useState(false);
  const [error, setError] = useState(null);

  const cargar = useCallback(async () => {
    setError(null);
    try {
      const r = await getResumenCampana(campana.id, { lote_id: lote.id });
      setParticipantes(r.usuarios || []);
      setSeleccion([]);
    } catch (e) {
      setError(e?.response?.data?.error || 'No se pudieron cargar las personas del lote.');
    }
  }, [campana.id, lote.id]);

  useEffect(() => { if (open) cargar(); }, [open, cargar]);

  function hecho(mensaje) {
    toast.success(mensaje);
    cargar();
    onCambio?.();
  }

  async function quitar(confirmar = false) {
    setTrabajando(true); setError(null);
    try {
      const r = await quitarDelLote(campana.id, lote.id, seleccion, confirmar);
      setImpacto(null);
      hecho(r.citas_canceladas > 0
        ? `Quitadas ${r.quitadas.length}. Se han cancelado ${r.citas_canceladas} cita(s) y se ha avisado.`
        : `Quitadas ${r.quitadas.length}.`);
    } catch (e) {
      const d = e?.response?.data;
      if (d?.codigo === 'IMPACTO') setImpacto(d.impacto.citas_a_cancelar);
      else setError(d?.personas?.length
        ? `${d.error} (${d.personas.map((p) => p.nombre || `#${p.persona_id ?? p}`).join(', ')})`
        : (d?.error || 'No se pudo quitar.'));
    } finally { setTrabajando(false); }
  }

  async function mover() {
    setTrabajando(true); setError(null);
    try {
      const r = await moverEntreLotes(campana.id, lote.id, seleccion, Number(destino));
      setDestino('');
      hecho(`Movidas ${r.movidas.length} al lote ${r.destino.numero}.${r.notificadas ? ` Se ha avisado a ${r.notificadas}.` : ''}`);
    } catch (e) {
      setError(e?.response?.data?.error || 'No se pudo mover.');
    } finally { setTrabajando(false); }
  }

  async function anadir() {
    setTrabajando(true); setError(null);
    try {
      const r = await anadirAlLote(campana.id, lote.id, nuevas.ids);
      setNuevas({ ids: [], cache: {} });
      const fuera = r.excluidas.length ? ` ${r.excluidas.length} ya estaban en la campaña.` : '';
      hecho(`Añadidas ${r.incluidas.length}.${r.notificadas ? ` Se les ha avisado.` : ''}${fuera}`);
    } catch (e) {
      setError(e?.response?.data?.error || 'No se pudo añadir.');
    } finally { setTrabajando(false); }
  }

  const otrosLotes = (lotes || []).filter((l) => l.id !== lote.id);
  const columnas = [
    {
      key: 'nombre', header: 'Persona',
      render: (u) => (
        <div className="min-w-0">
          <div className="text-v25-ink-900 truncate">{u.nombre_completo}</div>
          {u.email && <div className="text-[11px] text-v25-ink-400 truncate">{u.email}</div>}
        </div>
      ),
    },
    {
      key: 'estado', header: 'Estado',
      render: (u) => <Badge tone={(ESTADO[u.estado] || {}).tono || 'info'}>{(ESTADO[u.estado] || {}).texto || u.estado}</Badge>,
    },
    {
      key: 'cita', header: 'Cita',
      render: (u) => (u.cita
        ? <span className="tabular-nums">{u.cita.fecha} · {u.cita.hora_inicio || etiquetaDe(MODALIDAD, u.cita.modalidad).label}</span>
        : <span className="text-v25-ink-400">—</span>),
    },
  ];

  return (
    <>
      <Modal
        open={open}
        onClose={trabajando ? () => {} : onClose}
        size="xl"
        title={`Personas del lote ${lote.numero} · ${lote.nombre}`}
        subtitle={campana.estado === 'Activa' ? 'Campaña activa: las nuevas reciben el aviso al añadirlas.' : 'Campaña pausada: el aviso sale al reanudarla.'}
        footer={<Button variant="outline" onClick={onClose} disabled={trabajando}>Cerrar</Button>}
      >
        <div className="space-y-4">
          {error && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-md p-3 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" /> {error}
            </div>
          )}

          {participantes === null ? (
            <div className="text-sm text-v25-ink-500"><Loader2 className="w-4 h-4 inline-block animate-spin mr-2" />Cargando…</div>
          ) : (
            <>
              <div className="flex flex-wrap items-end gap-2">
                <Button variant="outline" size="sm" onClick={() => quitar(false)} disabled={trabajando || !seleccion.length}>
                  <UserMinus className="w-3.5 h-3.5" /> Quitar{seleccion.length ? ` (${seleccion.length})` : ''}
                </Button>
                {otrosLotes.length > 0 && (
                  <>
                    <Select label="Mover a" value={destino} onChange={(e) => setDestino(e.target.value)} wrapperClassName="min-w-[200px]">
                      <option value="">Elige un lote</option>
                      {otrosLotes.map((l) => <option key={l.id} value={l.id}>Lote {l.numero} · {l.nombre}</option>)}
                    </Select>
                    <Button variant="outline" size="sm" onClick={mover} disabled={trabajando || !seleccion.length || !destino}>
                      <ArrowRightLeft className="w-3.5 h-3.5" /> Mover{seleccion.length ? ` (${seleccion.length})` : ''}
                    </Button>
                  </>
                )}
                <span className="text-xs text-v25-ink-400 ml-auto">Una persona con la intervención realizada no se puede quitar.</span>
              </div>
              <Table
                columns={columnas}
                rows={participantes}
                selectable
                selected={seleccion}
                onSelect={setSeleccion}
                emptyState={<EmptyState variant="minimal" title="Este lote no tiene personas." />}
              />
            </>
          )}

          <div className="border-t border-v25-line pt-4 space-y-3">
            <div className="flex items-center justify-between gap-3">
              <h3 className="text-sm font-medium text-v25-ink-900 flex items-center gap-2"><UserPlus className="w-4 h-4" /> Añadir personas a este lote</h3>
              <Button size="sm" onClick={anadir} disabled={trabajando || !nuevas.ids.length}>
                {trabajando && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Añadir{nuevas.ids.length ? ` ${nuevas.ids.length}` : ''}
              </Button>
            </div>
            <SelectorPersonas
              rol="usuario"
              ambito={campana.ambito}
              excluirIds={[]}
              seleccionados={nuevas.ids}
              cache={nuevas.cache}
              onAdd={(p) => setNuevas((m) => (m.ids.includes(p.id) ? m : { ids: [...m.ids, p.id], cache: { ...m.cache, [p.id]: p } }))}
              onRemove={(id) => setNuevas((m) => ({ ...m, ids: m.ids.filter((x) => x !== id) }))}
              errores={[]}
              enAsistente={false}
              campanaId={campana.id}
            />
          </div>
        </div>
      </Modal>

      <Modal
        open={!!impacto}
        onClose={trabajando ? () => {} : () => setImpacto(null)}
        size="md"
        tone="danger"
        title="Quitar también cancela citas"
        footer={(
          <>
            <Button variant="outline" onClick={() => setImpacto(null)} disabled={trabajando}>Volver</Button>
            <Button variant="destructive" onClick={() => quitar(true)} disabled={trabajando}>
              {trabajando && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Cancelar citas y quitar
            </Button>
          </>
        )}
      >
        <p className="text-sm text-v25-ink-700 mb-2">Estas personas tienen una cita reservada. Si las quitas, la cita se cancela y se les avisa a ellas y a su técnico:</p>
        <ul className="text-sm text-v25-ink-700 space-y-1">
          {(impacto || []).map((c) => (
            <li key={c.cita_id}>{c.nombre} — <span className="tabular-nums">{c.fecha} · {c.hora_inicio}</span></li>
          ))}
        </ul>
      </Modal>
    </>
  );
}
